import { Box, Typography, CircularProgress } from "@mui/material"
import { Timeline, timelineItemClasses, timelineSeparatorClasses } from "@mui/lab"
import { useEffect, useRef, useState } from "react"
import { makeStyles } from "tss-react/mui"

import { RoadmapImplementationType, RoadmapItemType } from "../newsTypes"
import { fetchRoadmapByPeriod, FetchRoadmapResponse, NewsQuery } from "../newsApi"
import { RoadmapFeatureCard } from "./RoadmapFeatureCard"
import { useApiQuery } from "../../../core/api/useApiQuery"
import { RoadmapTimelineItem } from "./RoadmapTimelineItem"
import { useActiveScrollItem } from "../useActiveScrollItem"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { LoadingSpinner } from "../../../shared/components/loading/LoadingSpinner"

const useStyles = makeStyles()((theme) => ({
    root: {
        padding: "0 1rem",
    },
    layout: {
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: "1.5rem",
        alignItems: "start",
    },
    mainContent: {
        flex: "0 0 66%",
        paddingBottom: "35rem",
        order: 1,
    },
    error: {
        color: theme.palette.error.main,
        textAlign: "center",
        padding: theme.spacing(4),
    },
    timelineContainer: {
        position: "sticky",
        top: "4rem",
        order: 2,
        overflowY: "auto",
    },
}))

const selectNews = (data: FetchRoadmapResponse) => {
    return data.roadmaps.filter(
        (item) =>
            item.roadmapDetails.itemType === RoadmapItemType.IMPLEMENTED &&
            item.roadmapDetails.implementationType === RoadmapImplementationType.FEATURE,
    )
}

export default function NewFeatures() {
    const { classes } = useStyles()
    const {
        data: features,
        isLoading,
        error,
    } = useApiQuery(fetchRoadmapByPeriod, {
        queryName: NewsQuery.LIST,
        dependencies: {},
        select: selectNews,
    })
    const featureRefs = useRef<Map<string, HTMLDivElement>>(new Map())
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const { activeItemId, setActiveItemId } = useActiveScrollItem(featureRefs, features)

    useEffect(() => {
        if (features && features.length > 0 && !activeItemId) {
            setActiveItemId(features[0].permaLink)
        }
    }, [features, activeItemId])

    const handleTimelineClick = (permaLink: string) => {
        const element = featureRefs.current.get(permaLink)
        if (element) {
            setActiveItemId(permaLink)
            window.scrollTo({ top: element.offsetTop + 150, behavior: "smooth" })
        }
    }

    if (isLoading) {
        return (
            <Box className={classes.root}>
                <LoadingSpinner />
            </Box>
        )
    }

    if (error) {
        return (
            <Box className={classes.root}>
                <LocalizedStrict id="news-new-load-error">
                    <Typography className={classes.error}>Error loading roadmap features</Typography>
                </LocalizedStrict>
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.layout}>
                <Box className={classes.mainContent} ref={scrollContainerRef}>
                    {features?.map((item) => (
                        <RoadmapFeatureCard
                            key={item.permaLink}
                            item={item}
                            isUser
                            ref={(el) => {
                                if (el) {
                                    featureRefs.current.set(item.permaLink, el)
                                } else {
                                    featureRefs.current.delete(item.permaLink)
                                }
                            }}
                        />
                    ))}
                </Box>

                <Box className={classes.timelineContainer}>
                    <Timeline
                        sx={{
                            padding: 0,
                            position: "relative",
                            top: "-1rem",
                            [`& .${timelineItemClasses.root}:before`]: {
                                flex: 0,
                            },
                            [`& .${timelineSeparatorClasses.root}`]: {
                                position: "relative",
                                top: "1rem",
                            },
                        }}
                    >
                        {features?.map((item, index) => (
                            <RoadmapTimelineItem
                                key={item.permaLink}
                                item={item}
                                isUser
                                isLast={index === features.length - 1}
                                isActive={activeItemId === item.permaLink}
                                onClick={() => handleTimelineClick(item.permaLink)}
                            />
                        ))}
                    </Timeline>
                </Box>
            </Box>
        </Box>
    )
}
